import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Plus, Upload } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import { CrmTabs } from "@/components/admin/CrmTabs";
import { api, downloadCsv } from "@/lib/api";
import { inputCls, Field, Btn, GhostBtn, Th, Td, TableWrap, EmptyRow } from "@/components/admin/ui";

const blank = {
  owner_name: "", company: "", email: "", phone: "", mc_number: "", dot_number: "",
  equipment: "", preferred_lanes: "", factoring_company: "", availability: "available", notes: "", fee_pct: "9",
};

const AVAIL = ["available", "under_load", "off"];

export default function CrmCarriers() {
  const [carriers, setCarriers] = useState([]);
  const [q, setQ] = useState("");
  const [avail, setAvail] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (avail) params.availability = avail;
    api.get("/crm/carriers", { params }).then(({ data }) => setCarriers(data)).catch(() => {});
  }, [q, avail]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/crm/carriers", { ...form, fee_pct: Number(form.fee_pct) || 9 });
      toast.success("Carrier added");
      setForm(blank);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add carrier");
    } finally {
      setSaving(false);
    }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/crm/carriers/import", fd);
      toast.success(`Imported ${data.created} carriers (${data.skipped} skipped)`);
      load();
    } catch {
      toast.error("Import failed — check your CSV columns");
    } finally {
      e.target.value = "";
    }
  };

  const exportCsv = () =>
    downloadCsv(
      `carriers-${new Date().toISOString().slice(0, 10)}.csv`,
      ["carrier_code", "owner_name", "company", "email", "phone", "mc_number", "dot_number", "equipment", "preferred_lanes", "factoring_company", "availability", "fee_pct", "notes"],
      carriers.map((c) => [c.carrier_code, c.owner_name, c.company, c.email, c.phone, c.mc_number, c.dot_number, c.equipment, c.preferred_lanes, c.factoring_company, c.availability, c.fee_pct, c.notes])
    );

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="crm-carriers-page">
      <AdminNav />
      <CrmTabs />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-3xl">Carriers</h1>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1" data-testid="carriers-count">
              {carriers.length} on roster
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <input data-testid="carriers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, company, MC..." className={`${inputCls} w-56`} />
            <select data-testid="carriers-availability-filter" value={avail} onChange={(e) => setAvail(e.target.value)} className={`${inputCls} w-auto bg-[#0B0B0B]`}>
              <option value="">All</option>
              {AVAIL.map((a) => <option key={a} value={a}>{a.replace("_", " ")}</option>)}
            </select>
            <GhostBtn data-testid="carriers-import-button" onClick={() => fileRef.current?.click()}><Upload size={13} /> Import</GhostBtn>
            <GhostBtn data-testid="carriers-export-button" onClick={exportCsv}><Download size={13} /> Export</GhostBtn>
            <Btn data-testid="carriers-add-toggle" onClick={() => setShowForm(!showForm)}><Plus size={13} /> Add Carrier</Btn>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" data-testid="carriers-import-input" onChange={importCsv} />
          </div>
        </div>

        {showForm && (
          <form onSubmit={save} className="border border-white/15 bg-[#101010] p-5 sm:p-7 mb-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="carrier-add-form">
            <Field label="Owner Name *" id="c-owner"><input id="c-owner" data-testid="carrier-owner-input" required value={form.owner_name} onChange={set("owner_name")} className={inputCls} /></Field>
            <Field label="Company *" id="c-company"><input id="c-company" data-testid="carrier-company-input" required value={form.company} onChange={set("company")} className={inputCls} /></Field>
            <Field label="Email" id="c-email"><input id="c-email" data-testid="carrier-email-input" type="email" value={form.email} onChange={set("email")} className={inputCls} /></Field>
            <Field label="Phone" id="c-phone"><input id="c-phone" data-testid="carrier-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} /></Field>
            <Field label="MC Number" id="c-mc"><input id="c-mc" data-testid="carrier-mc-input" value={form.mc_number} onChange={set("mc_number")} className={inputCls} /></Field>
            <Field label="DOT Number" id="c-dot"><input id="c-dot" data-testid="carrier-dot-input" value={form.dot_number} onChange={set("dot_number")} className={inputCls} /></Field>
            <Field label="Equipment" id="c-equip"><input id="c-equip" data-testid="carrier-equipment-input" value={form.equipment} onChange={set("equipment")} className={inputCls} placeholder="Dry Van, Reefer..." /></Field>
            <Field label="Preferred Lanes" id="c-lanes"><input id="c-lanes" data-testid="carrier-lanes-input" value={form.preferred_lanes} onChange={set("preferred_lanes")} className={inputCls} /></Field>
            <Field label="Factoring Company" id="c-factoring"><input id="c-factoring" data-testid="carrier-factoring-input" value={form.factoring_company} onChange={set("factoring_company")} className={inputCls} /></Field>
            <Field label="Availability" id="c-avail">
              <select id="c-avail" data-testid="carrier-availability-select" value={form.availability} onChange={set("availability")} className={`${inputCls} bg-[#101010]`}>
                {AVAIL.map((a) => <option key={a} value={a}>{a.replace("_", " ")}</option>)}
              </select>
            </Field>
            <Field label="Dispatch Fee %" id="c-fee"><input id="c-fee" data-testid="carrier-fee-input" type="number" step="0.1" min="0" max="100" value={form.fee_pct} onChange={set("fee_pct")} className={inputCls} /></Field>
            <Field label="Notes" id="c-notes"><input id="c-notes" data-testid="carrier-notes-input" value={form.notes} onChange={set("notes")} className={inputCls} /></Field>
            <div className="sm:col-span-2 lg:col-span-3">
              <Btn type="submit" data-testid="carrier-save-button" disabled={saving}>{saving ? "Saving..." : "Save Carrier"}</Btn>
            </div>
          </form>
        )}

        <TableWrap testid="carriers-table">
          <thead>
            <tr className="border-b border-white/10">
              <Th>ID</Th><Th>Carrier</Th><Th>Contact</Th><Th>MC / DOT</Th><Th>Equipment</Th><Th>Availability</Th><Th>Fee %</Th>
            </tr>
          </thead>
          <tbody>
            {carriers.length === 0 ? (
              <EmptyRow cols={7}>No carriers yet — add one or import a CSV</EmptyRow>
            ) : (
              carriers.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`carrier-row-${c.id}`}
                  onClick={() => navigate(`/admin/crm/carriers/${c.id}`)}
                  className="border-b border-white/5 hover:bg-white/[0.04] cursor-pointer transition-colors duration-200"
                >
                  <Td className="font-mono text-xs text-neutral-400">{c.carrier_code}</Td>
                  <Td>
                    <div className="font-semibold">{c.company}</div>
                    <div className="text-neutral-500 text-xs">{c.owner_name}</div>
                  </Td>
                  <Td>
                    <div className="text-xs text-neutral-300">{c.email || "—"}</div>
                    <div className="text-xs text-neutral-500">{c.phone || ""}</div>
                  </Td>
                  <Td className="font-mono text-xs text-neutral-400">{[c.mc_number, c.dot_number].filter(Boolean).join(" / ") || "—"}</Td>
                  <Td className="text-neutral-300">{c.equipment || "—"}</Td>
                  <Td>
                    <span className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 ${c.availability === "available" ? "bg-white text-black" : "border border-white/30 text-neutral-300"}`}>
                      {c.availability?.replace("_", " ")}
                    </span>
                  </Td>
                  <Td className="font-mono text-xs">{c.fee_pct}%</Td>
                </tr>
              ))
            )}
          </tbody>
        </TableWrap>
      </main>
    </div>
  );
}
